import { readViewerDeviceId, takeReturnTo, writeLoginError, writeToken } from './session.js';

/**
 * GIS redirect 의 착지점 (v17 R9 · `docs/40` D12).
 *
 * **서버가 여기로 302 한다.** 토큰은 **프래그먼트**에 실려 온다 — 질의 문자열은 서버
 * 로그·`Referer`·프록시에 남지만 프래그먼트는 서버로 전송되지 않는다.
 *
 * **서버의 `google-redirect.ts` 와 같은 경로 문자열이어야 한다.** 어긋나면 서버는 302 를
 * 내고 앱은 그 주소를 시청 화면으로 받아 **토큰을 그냥 버린다** — 증상은 「로그인했는데
 * 로그인이 안 돼 있다」 하나다.
 */
export const AUTH_CALLBACK_PATH = '/auth/callback';

export type AuthCallback = { token: string } | { error: string };

/**
 * 프래그먼트를 읽는다. **순수 함수다** — 이 판정이 node 환경에서 검사되지 않으면
 * 실기에서만 드러난다 (`routing.ts`·`login-mode.ts` 와 같은 자리).
 */
export function readAuthCallback(hash: string): AuthCallback | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('t');
  if (token !== null && token !== '') return { token };
  const error = params.get('e');
  if (error !== null && error !== '') return { error };
  return null;
}

/**
 * 콜백이면 소비하고 **되돌아갈 곳으로 페이지를 옮긴다.** 아니면 `false`.
 *
 * ── React 가 서기 전에 부른다 ────────────────────────────────
 *
 * 이 자리에서 하는 일은 저장소 쓰기와 이동뿐이라 화면이 필요 없고, **화면을 그린 뒤에
 * 하면 그 한 프레임이 시청 화면**이다 — 토큰을 들고 방송이 잠깐 뜬다.
 *
 * ── 프래그먼트를 반드시 지운다 ───────────────────────────────
 *
 * `history.replaceState` 와 `location.replace` 를 **둘 다** 한다. 하나만 하면 뒤로 가기로
 * 토큰이 되살아난다 (`docs/40` §8-⑮) — 프래그먼트는 서버로 가지 않을 뿐 히스토리에는 남는다.
 */
export function consumeAuthCallback(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.location.pathname.replace(/\/+$/, '') !== AUTH_CALLBACK_PATH) return false;

  const result = readAuthCallback(window.location.hash);
  if (result !== null && 'token' in result) {
    writeToken(result.token);
  } else {
    /*
     * **실패도 말한다.** 서버는 JSON 오류를 그리지 않고 이리로 되돌리므로(흰 화면에
     * 중괄호가 뜨는 것을 막는다), 화면이 대신 말해야 한다. 코드는 take-once 로 두고
     * 로그인 입구가 그 자리에서 문구로 옮긴다.
     */
    writeLoginError(result === null ? 'auth' : result.error);
  }

  window.history.replaceState(null, '', window.location.pathname);
  window.location.replace(takeReturnTo() ?? '/');
  return true;
}

/**
 * redirect 모드에 넘길 값 (v17 R9 · v19 개정).
 *
 * **`loginUri` 는 구글 콘솔의 「승인된 리디렉션 URI」와 글자 하나까지 같아야 한다.**
 * 다르면 `redirect_uri_mismatch` 이고, 증상은 「구글 화면까지 갔다가 오류로 끝난다」다.
 * 저장소의 규칙이 「리디렉션 URI 는 비워 둔다」였는데 **v17 이 그것을 거짓으로 만든다**
 * (`docs/18` §3.1-2 · `docs/40` §8-⑭).
 *
 * ── ★ **서버 주소가 아니라 앱 오리진이다** (v19) ──────────────
 *
 * GIS 는 `g_csrf_token` 쿠키를 **지금 이 문서의 오리진**에 심고, 구글은 같은 값을 본문에
 * 실어 `loginUri` 로 POST 한다. 서버는 둘을 대조한다 — **그래서 콜백이 쿠키와 같은
 * 호스트에 있어야 한다.**
 *
 * v17 은 여기에 `config.serverUrl`(= `bloom-api.bloomlabs.tv`)을 넘겼고, 앱은
 * `bloom.bloomlabs.tv` 라 **쿠키가 따라가지 않았다.** 서버 로그에
 * `CSRF 대조 실패 (쿠키 없음)` 이 남고 **화면에는 아무 오류도 뜨지 않는다**
 * (`docs/39` §12.4). 노트북과 Android 는 팝업으로 빼서 살렸지만, **iOS 홈 화면 앱은
 * 이 경로뿐이라** 그것으로는 풀리지 않는다.
 *
 * **개발에서는 원래 우연히 됐다** — 쿠키는 **호스트로 갈리고 포트를 보지 않아서**
 * `localhost:5174` 의 쿠키가 `localhost:3000` 으로 갔다. 도메인 배포에서만 갈렸고,
 * 그래서 **노트북에서는 재현되지 않는 결함**이었다.
 *
 * 앱 오리진의 그 경로는 **Caddy 가 BLOOM Server 로 넘긴다**(`Caddyfile` 둘 다).
 * 개발에서는 `vite.config.ts` 의 프록시가 같은 일을 한다.
 */
export function googleRedirectOptions(appOrigin: string): {
  loginUri: string;
  state?: string;
} {
  const base = appOrigin.replace(/\/+$/, '');
  const deviceId = readViewerDeviceId();
  return {
    loginUri: `${base}/v1/auth/google/callback`,
    ...(deviceId === null || deviceId.trim() === '' ? {} : { state: deviceId.trim() }),
  };
}
